import states from "../../constants/states.js";
import React, { useState, useContext, useEffect} from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Row, Col,
} from "reactstrap";
import { LocationModalContext } from "../../utilities/Contexts/LocationModalContext.js";
import { locationModalTypes } from "../../constants/treeState.js";
import { useInsertLocationMutation } from '../../utilities/mutations/useInsertLocationMutation';
import { useUpdateLocationMutation } from '../../utilities/mutations/useUpdateLocationMutation';
import { useLocationQuery } from '../../utilities/queries/useLocationQuery';
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import { errorToast, successToast} from '../../constants/toast';
import PreventEnterSubmitForm from "../../components/Forms/PreventEnterSubmitForm.js"
import Button from "../../components/UIButton/UIButton.js";
import ActionModalHeader from "../../components/ActionModalHeader/ActionModalHeader.js";
import PostalZipCodeControl from "../../components/Addresses/PostalZipCodeControl.js";
import { isValidPostalZipCode } from "../../utilities/addressHelper.js";
import BootstrapSelect from "../../components/BootstrapSelect/BootstrapSelect.js";
import './CreateEditLocationModal.css';
import Spinner from "../../components/UISpinner/UISpinner.js";
import { getTenantCountry } from "../../utilities/appStore.js";
import countries from "../../constants/countries.js";
import provinces from "../../constants/provinces.js";

const emptyFormData = {
    locationName: "",
    description: "",
    accountId: undefined,
    displayAccountId: undefined,
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    defaultLanguage: "",
};
const CreateEditLocationModal = ({ children, onClosed, makeToast }) => {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const [formData, setFormData] = useState(emptyFormData);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [intialParentId, setIntialParentId] = useState(null);
    const { selectedAccountId, toggleLocationModal, locationModalType, modalTreeCheckedItem, expandParentNodeAfterAddingData } = useContext(LocationModalContext);

    const { isPending: isInsertLoading, mutateAsync: insertAsync, error: insertError, reset: insertReset } = useInsertLocationMutation();
    const { isPending: isUpdateLoading, mutateAsync: updateAsync, error: updateError, reset: updateReset } = useUpdateLocationMutation();
    const { data, isSuccess, isLoading: isLocationLoading } = useLocationQuery({ locationId: selectedAccountId, enable: (!!selectedAccountId && locationModalType === locationModalTypes.Edit) });
    const [zipcodeIsValid, setZipcodeIsValid] = useState(false);
    const [phoneNumberIsValid, setPhoneNumberIsValid] = useState(true);

    const tenantCountry = getTenantCountry();
    
    
    const getLoadingMessage = () => {
        if (isInsertLoading) return "Creating location...";
        if (isUpdateLoading) return "Updating location...";
        if (isLocationLoading) return "Loading location...";
        return "";
    };
    const isLoading = isInsertLoading || isUpdateLoading || isLocationLoading;
    const hasError = insertError || updateError;

    const checkForNull = (x) => x?.response?.data === null || x?.response?.data === undefined || x?.response?.data?.trim() === '';
    let errorMessage = '';
    if (hasError) {
        if (!checkForNull(updateError)) {
            errorMessage = updateError.response.data;
        }
        else if (!checkForNull(insertError)) {
            errorMessage = insertError.response.data;
        }
        else {
            errorMessage = 'An error has occurred while saving. Please try again later.'
        }
    }

    const languageOptions = [
        { value: "", label: "" },
        { value: "English", label: "English" },
        { value: "Spanish", label: "Spanish" },
        { value: "French", label: "French" }
    ];

    const successCall = () => {
        makeToast(locationModalType === locationModalTypes.Add ? "Location Created" : "Location updated", successToast);
        toggleLocationModal();
    };

    const failedCall = () => {
        makeToast(locationModalType === locationModalTypes.Add ? "Error Creating Location" : "Error updating Location", errorToast);
    };
    
    useEffect(() => {
        if (isSuccess) {
            setFormData({ ...emptyFormData, ...data, displayAccountId: `L-${data.accountId}` });
        }
    }, [isSuccess, data, selectedAccountId]);

    useEffect(() => {
        if (locationModalType === locationModalTypes.Add) {
            setFormData({ ...emptyFormData});
        }
    }, [selectedAccountId, locationModalType]);

    useEffect(() => {
        if (modalTreeCheckedItem.length === 1) {
            setIntialParentId(modalTreeCheckedItem)
        }      
    }, []);


    useEffect(() => {
        const validatePhoneNumber = (phoneNumber) => {
            let isValid = false;

            try {
                //Phone not required on location
                if (phoneNumber.trim() === '') {
                    isValid = true;
                }
                //Reject if phoneNumber contains invalid characters
                else if (!/^[\d\s()+\-]+$/.test(phoneNumber)) {
                    isValid = false;
                }
                else {
                    const number = phoneUtil.parse("+1" + phoneNumber);
                    isValid = phoneUtil.isValidNumber(number);
                }
            } catch {
                isValid = false;
            }

            setPhoneNumberIsValid(isValid);
        };



        const validateZip = (zipcode) => {
            let isValid = isValidPostalZipCode(zipcode, tenantCountry);
            setZipcodeIsValid(isValid);
        };

        if (formData) {
         validatePhoneNumber(formData.phone);
         validateZip(formData.zipCode);
        }

    }, [formData]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };
    const formatPhoneIfValid = (phone) => {
        try {
            const parsed = phoneUtil.parse("+1" + phone);
            if (phoneUtil.isValidNumber(parsed)) {
                return phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL);
            }
        } catch {
            // fall through to return original
        }
        return phone;
    }
    const handlePhoneBlur = () => {
        if (!phoneNumberIsValid) return;
        setFormData({ ...formData, phone: formatPhoneIfValid(formData.phone.trim()) });
    };

    const trimString  = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value.trim()
        }));

    }

    const cancelHandler = (event) => {
        event.preventDefault();
        insertReset();
        updateReset();
        setShowConfirmModal(false);
        toggleLocationModal();
    }

    const cancelConfirm = (e) => {
        e.preventDefault();
        setShowConfirmModal(false);
    }

    const confirmSave = (e) => {
        e.preventDefault();
        setShowConfirmModal(false);
        onSave(e, true)
    }

    const onSave = async (e, isConfirm) => {
        e.preventDefault();

        if (!phoneNumberIsValid) {
            const toastMsg = 'Please enter a valid phone number';
            makeToast(toastMsg, errorToast);
            return;
        }
        else if (!zipcodeIsValid) {
            const toastMsg = 'Please enter a valid postal code';
            makeToast(toastMsg, errorToast);
            return;
        }

        if (locationModalType === locationModalTypes.Edit && !isConfirm && intialParentId[0] !== modalTreeCheckedItem[0]) {
            setShowConfirmModal(true);
            return;
        }

        if (formData.locationName?.trim().length === 0) {
            const toastMsg = 'Please enter a valid name';
            makeToast(toastMsg, errorToast);
            return;
        }

        if (formData.address1?.trim().length === 0) {
            const toastMsg = 'Please enter a valid street address';
            makeToast(toastMsg, errorToast);
            return;
        }

        if (formData.city?.trim().length === 0) {
            const toastMsg = 'Please enter a valid city';
            makeToast(toastMsg, errorToast);
            return;
        }
        if (formData.description?.trim().length === 0) {
            const toastMsg = 'Please enter a valid description';
            makeToast(toastMsg, errorToast);
            return;
        }
        const submitData = {
            ...formData,
            phone: formatPhoneIfValid(formData.phone),
            parentAccountId: modalTreeCheckedItem[0],
            country: tenantCountry
        }

        if (locationModalType === locationModalTypes.Add) {
            await insertAsync({ location: submitData }, { onError: failedCall, onSuccess: successCall });
        }
        else {
            await updateAsync({ id: formData.accountId, location: submitData }, { onError: failedCall, onSuccess: successCall });
        }
        expandParentNodeAfterAddingData(modalTreeCheckedItem[0]);

    }
    const stateSectionOptions = tenantCountry === countries.Canada ? provinces : states;
    const stateLabel = tenantCountry === countries.Canada ? "Province *" : "State *"
        

    return (
        <>
            <Modal className="location-edit-modal"
                data-testid={"location-modal-" + locationModalType}
                unmountOnClose
                backdrop="static"
                size="xl"
                onClosed={onClosed}
                isOpen={true}
            >
                <PreventEnterSubmitForm onSubmit={onSave}>
                    {/* Custom Header with Buttons */}
                    <ActionModalHeader
                        title={`${locationModalType} Location`}
                        onCancel={cancelHandler}
                        cancelButtonDisabled={isLoading}
                        actionButton={
                            <Button variant="submit" size="medium" disabled={isLoading}>Save Changes</Button>
                        }
                    />
                    <ModalBody className={`pt-0 pr-0 ${locationModalType === locationModalTypes.Edit ? 'pb-3' : ''}`}>
                        {isLoading && <Spinner text={getLoadingMessage()} />}
                        {!isLoading && <Row className="d-flex align-items-stretch">
                            <Col className="pt-4" md={6}> {/* Left Side - Form Fields */}

                                {[
                                    { label: "Location Name *", name: "locationName", isRequired: true, onBlur: trimString  },
                                    { label: "Description *", name: "description", isRequired: true, onBlur: trimString },
                                    { label: "Account ID", name: "displayAccountId", disabled: true, isHidden: locationModalType === locationModalTypes.Add },
                                    { label: "Address 1 *", name: "address1", isRequired: true, onBlur: trimString },
                                    { label: "Address 2", name: "address2", isRequired: false },
                                    { label: "City *", name: "city", isRequired: true, onBlur: trimString },
                                ].map(({ label, name, disabled, isHidden, isRequired, onBlur}) => (
                                    <FormGroup key={name} row className="mb-1 align-items-center">
                                        <Label for={name} hidden={isHidden} sm={4} className="col-form-label">{label}</Label>
                                        <Col sm={8}>
                                            <Input
                                                type="text"
                                                id={name}
                                                name={name}
                                                value={formData[name]}
                                                onChange={handleChange}
                                                disabled={disabled}
                                                hidden={isHidden}
                                                required={isRequired}
                                                onBlur={onBlur}
                                                className="form-control-sm"
                                            />
                                        </Col>
                                    </FormGroup>
                                ))}
                                {/* Dropdown Fields */}
                                <FormGroup row className="mb-1 align-items-center">
                                    <Label sm={4} className="col-form-label">{stateLabel}</Label>
                                    <Col sm={8} className="">
                                        <BootstrapSelect
                                            id="state"
                                            name="state"
                                            required
                                            options={stateSectionOptions}
                                            size="sm"
                                            value={stateSectionOptions.find(opt => opt.value === formData.state ?? null)}
                                            onChange={(val) => {handleChange({target: { id: "state", value: val.value }})}}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup key="zipCode" row className="mb-1 align-items-center">
                                    <Label for="zipCode" sm={4} className="col-form-label">Postal Code *</Label>
                                    <Col sm={8}>
                                        <PostalZipCodeControl 
                                            id="zipCode"
                                            name="zipCode"
                                            defaultValue={formData["zipCode"]}
                                            onChange={handleChange}
                                            valid={zipcodeIsValid}
                                            invalid={!zipcodeIsValid}
                                            required
                                            bsSize="sm"
                                            region={tenantCountry}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup key="phone" row className="mb-1 align-items-center">
                                    <Label for="phone" sm={4} className="col-form-label">Phone #</Label>
                                    <Col sm={8}>
                                        <Input
                                            type="text"
                                            id="phone"
                                            name="phone"
                                            value={formData["phone"]}
                                            onChange={handleChange}
                                            valid={phoneNumberIsValid}
                                            invalid={!phoneNumberIsValid}
                                            onBlur={handlePhoneBlur}
                                            className="form-control-sm"
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row className="mb-1 align-items-center">
                                    <Label sm={4} className="col-form-label">Default Language</Label>
                                    <Col sm={8}>
                                        <BootstrapSelect
                                            name="defaultLanguage"
                                            value={languageOptions.find(opt => opt.value === formData.defaultLanguage)}
                                            options={languageOptions}
                                            onChange={(opt) =>
                                                handleChange({ target: { id: "defaultLanguage", value: opt.value } })
                                            }
                                            size="sm"
                                        />
                                    </Col>
                                </FormGroup>

                            </Col>

                            {/* Right Side - Hierarchy Assignment */}
                            <Col md={6}>
                                <FormGroup row className="align-items-center">
                                    <Col sm={12}>
                                        <div className="mini-tree-container" style={{ backgroundColor: "#e9ecef", minHeight: "500px" }}>
                                            {children}
                                        </div>
                                    </Col>
                                </FormGroup>
                            </Col>
                            {hasError && <div className="text-center text-danger w-100" role="alert">{errorMessage}</div>}
                        </Row>}
                    </ModalBody>
                </PreventEnterSubmitForm>
            </Modal>
            <Modal isOpen={showConfirmModal} data-testid="confirm-modal">
                <ModalHeader className="bg-light w-100 d-flex justify-content-center">
                    <div className="text-primary text-center">
                        Confirm Location Reassignment
                    </div>
                    
                </ModalHeader>
                <ModalBody className="text-center border-bottom border-top">
                    Are you sure you want to move this location to a different organization in your Hierarchy?
                </ModalBody>
                <ModalFooter className="d-flex justify-content-center">
                    <div className="text-warning">
                        WARNING!
                    </div>
                    <div>
                        This action may affect user's access to menu items, printer profiles, devices and other data.
                    </div>
                    <div>
                        <Button variant="info" onClick={cancelConfirm} className="me-2">Cancel</Button>
                        <Button onClick={confirmSave} variant="submit" disabled={isLoading}>Confirm</Button>
                    </div>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default CreateEditLocationModal;
